import React from 'react';
import { Helmet } from 'react-helmet-async';
import PageTransition from '../components/ui/PageTransition';
import SectionHeading from '../components/ui/SectionHeading';
import HeroSection from '../components/sections/HeroSection';

const PrivacyPage: React.FC = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Privacy Policy | Consiliuma Web Design & Development</title>
        <meta name="description" content="Our commitment to protecting your privacy and personal data. Learn about how we collect, use, and safeguard your information in compliance with international privacy laws." />
        <meta name="keywords" content="Privacy Policy, Data Protection, GDPR Compliance, Website Privacy" />
        <link rel="canonical" href="/privacy-policy" />
      </Helmet>

      <HeroSection
        title="Privacy Policy"
        subtitle="Our commitment to protecting your privacy and personal data"
        ctaText="Contact Us"
        ctaLink="/contact"
        backgroundImage="https://images.pexels.com/photos/5473298/pexels-photo-5473298.jpeg"
      />
      
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                Last updated: {new Date().toLocaleDateString('en-GB', { 
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>

              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">1. Introduction and Language Policy</h2>
                  <p>Welcome to Consiliuma's Privacy Policy. This document explains how we collect, use, and protect your personal information when you use our services. Please note that this privacy policy and all our services are provided exclusively in English. While we serve clients globally, we maintain English as our sole operating language to ensure clarity and consistency in our communications and legal documents.</p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">2. Information We Collect</h2>
                  <h3 className="text-xl font-bold mb-3">2.1 Personal Information</h3>
                  <p>We collect only the following types of personal information that you voluntarily provide to us:</p>
                  <ul className="list-disc ml-6 mb-4">
                    <li>Name and contact details</li>
                    <li>Email address</li>
                    <li>Phone number (if provided)</li>
                    <li>Business information (if applicable)</li>
                  </ul>

                  <h3 className="text-xl font-bold mb-3">2.2 No Tracking Policy</h3>
                  <p>We respect your privacy and do not:</p>
                  <ul className="list-disc ml-6">
                    <li>Track IP addresses</li>
                    <li>Collect device information</li>
                    <li>Use tracking cookies</li>
                    <li>Monitor user behavior</li>
                    <li>Use analytics tools</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">3. How We Use Your Information</h2>
                  <p>We use your information solely for:</p>
                  <ul className="list-disc ml-6">
                    <li>Providing our services</li>
                    <li>Communicating with you about your project</li>
                    <li>Processing your requests</li>
                    <li>Sending important service updates</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">4. International Data Transfers</h2>
                  <p>We operate globally with offices in the UK, USA, and Southeast Asia. Your data may be transferred to and processed in countries outside your residence. We ensure appropriate safeguards are in place through:</p>
                  <ul className="list-disc ml-6">
                    <li>Standard contractual clauses</li>
                    <li>Data processing agreements</li>
                    <li>Appropriate security measures</li>
                    <li>Regular security assessments</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">5. Data Protection Rights</h2>
                  <p>Under applicable data protection laws, you have the following rights:</p>
                  <ul className="list-disc ml-6">
                    <li>Right to access your personal data</li>
                    <li>Right to rectification of inaccurate data</li>
                    <li>Right to erasure ('right to be forgotten')</li>
                    <li>Right to restrict processing</li>
                    <li>Right to data portability</li>
                    <li>Right to object to processing</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">6. Security Measures</h2>
                  <p>We implement appropriate technical and organizational measures to protect your personal data, including:</p>
                  <ul className="list-disc ml-6">
                    <li>SSL/TLS encryption for data transmission</li>
                    <li>Secure data storage systems</li>
                    <li>Access controls and authentication</li>
                    <li>Regular security assessments</li>
                    <li>Staff training on data protection</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">7. Essential Cookies Only</h2>
                  <p>We use only essential cookies that are strictly necessary for website functionality. These cookies do not track or store personal information and are used solely for:</p>
                  <ul className="list-disc ml-6">
                    <li>Maintaining your session</li>
                    <li>Remembering basic preferences</li>
                    <li>Ensuring website security</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">8. Third-Party Services</h2>
                  <p>We use only essential third-party services for:</p>
                  <ul className="list-disc ml-6">
                    <li>Secure payment processing</li>
                    <li>Email communication</li>
                    <li>Website hosting</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">9. Data Retention</h2>
                  <p>We retain personal data only for as long as necessary to:</p>
                  <ul className="list-disc ml-6">
                    <li>Complete your project</li>
                    <li>Comply with legal obligations</li>
                    <li>Resolve disputes</li>
                    <li>Enforce our agreements</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">10. Changes to This Policy</h2>
                  <p>We may update this Privacy Policy from time to time. We will notify you of any changes by:</p>
                  <ul className="list-disc ml-6">
                    <li>Posting the new Privacy Policy on this page</li>
                    <li>Updating the "Last updated" date</li>
                    <li>Sending an email notification for significant changes</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">11. Contact Information</h2>
                  <p>For any questions about this Privacy Policy, please contact us:</p>
                  <ul className="list-none space-y-2">
                    <li>Email: hello@consiliuma.co.uk</li>
                    <li>Telegram: @Onebritabroad</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">12. Legal Basis for Processing</h2>
                  <p>We process personal data on the following legal bases:</p>
                  <ul className="list-disc ml-6">
                    <li>Consent</li>
                    <li>Contract performance</li>
                    <li>Legal obligations</li>
                    <li>Legitimate interests</li>
                  </ul>
                </section>

                <div className="mt-8 p-4 bg-gray-100 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Note: This privacy policy is available only in English. While we welcome clients from around the world, we maintain English as our sole operating language to ensure clear and consistent communication across all our services and legal documents.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default PrivacyPage;